import React, { useState, useEffect, useContext, useRef } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import { Grid, MenuItem, Checkbox, FormControlLabel } from '@material-ui/core';
import { toast } from "react-toastify";
import { CSVLink } from "react-csv";
import * as moment from "moment";
import { withStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';

import { styles } from './styles';
import PromoCode from 'apis/PromoCode/PromoCode';
import Loader from 'components/Loader/Loader';
import Context from 'context/context';
import Table from 'components/Table/Table';
import { exportHeaders } from "./export";
import useDebounce from "helpers/useDebounce";
import searchIcon from "assets/img/Search_Icon.png";

// material-ui icons
import CloseOutlinedIcon from '@material-ui/icons/CloseOutlined';
import EditIcon from '@material-ui/icons/Edit';

// custom TextField (same underline style as Create New Admin)
const CssTextField = withStyles({
  root: {
    '& label.Mui-focused': {
      background: 'transparent',
    },
    '& .MuiFormLabel-root': {
      background: 'transparent',
    },
    '& .MuiInputBase-input': {
      background: 'transparent',
    },
    '& .MuiInput-underline:before': {
      borderBottomColor: '#000 !important',
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: '#000 !important',
    },
  },
})(TextField);

// Removed All from plan options
const PLAN_OPTIONS = ['Yearly', 'Monthly', 'Trial'];

// stored in the DB as plain numbers (3,4,5,6); " Months" is added only for display
const TRIAL_PERIOD_OPTIONS = [3, 4, 5, 6];

const PROMO_CODE_REGEX = /^[A-Z0-9_-]{3,20}$/;

// amount with at most 2 decimals, up to 7 digits before the dot
const PRICE_REGEX = /^\d{1,7}(\.\d{1,2})?$/;

// what the user is allowed to type in the price box
const PRICE_TYPING_REGEX = /^\d{0,7}(\.\d{0,2})?$/;

const initialValues = {
  id: null,
  promo_code: '',
  promo_description: '',
  plan: '',
  trial_period: '',
  discounted_price: '', // optional
  start_date: '',
  end_date: '',
  is_valid: false, // One Year Application
  is_active: true,
};

// Created On / Created By removed from the list UI (still available in the export)
const headings = [
  'SNo',
  'Promo Code',
  'Description',
  'Plan',
  'Trial Period',
  'Discounted Price',
  'Start Date',
  'End Date',
  'Once Per Shipto',
  'Is Active',
  'Edit',
];

// "49" -> "49.00", empty / invalid -> ''
const toPriceInput = (value) => {
  if (value === null || value === undefined || String(value).trim() === '') return '';
  const n = Number(value);
  return Number.isNaN(n) ? String(value).trim() : n.toFixed(2);
};

// "49" -> "$49.00", empty -> '-'
const formatPrice = (value) => {
  const v = toPriceInput(value);
  return v === '' ? '-' : `$${v}`;
};

// the API returns dates as YYYY-MM-DD; keep only that part
const toDateInput = (value) => (value ? String(value).substring(0, 10) : '');

// Convert existing trial period values into dropdown values.
// Supports values such as 3, "3", and "3 Months".
const toTrialPeriodInput = (value) => {
  if (value === null || value === undefined || value === '') return '';

  const months = Number(String(value).replace(/months?/i, '').trim());

  return TRIAL_PERIOD_OPTIONS.includes(months) ? months : '';
};

function PromoCodeCreation() {
  const { state, dispatch } = useContext(Context);
  const {
    getPromoCodes,
    getPromoCodeExport,
    createPromoCode,
    updatePromoCode
  } = PromoCode();

  const classes = styles();
  const { t } = useTranslation();

  const [data, setData] = useState([]);
  const [offset, setOffset] = useState(1);
  const [count, setCount] = useState(0);

  const [searchValue, setSearchValue] = useState("");
  const searchBounceValues = useDebounce(searchValue);

  // filter panel: planDraft is what is ticked in the panel, planFilter is what is applied
  const [planFilter, setPlanFilter] = useState([]);
  const [planDraft, setPlanDraft] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [exportData, setExportData] = useState([]);

  // ignores slow responses that arrive after a newer request
  const requestRef = useRef(0);

  const isEdit = Boolean(form.id);
  const appliedFilterCount = planFilter.length;

  // load on first render, page change, debounced search and applied filter
  useEffect(() => {
    handleGetPromoCodes();
  }, [offset, searchBounceValues, planFilter]);

  async function handleGetPromoCodes() {
    const requestId = ++requestRef.current;

    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      let values = {
        offset: (offset - 1) * 10,
        limit: 10,
      };

      const search = String(searchBounceValues || "").trim();

      if (search) {
        values.searchValue = search;
      }

      if (planFilter.length > 0) {
        values.plans = planFilter;
      }

      let res = await getPromoCodes(values);

      if (requestId !== requestRef.current) return;

      if (res && res.data) {
        setData(res.data);
        setCount(Number(res.count) || 0);
      } else {
        setData([]);
        setCount(0);

        if (res?.message) {
          toast.error(res.message, { toastId: 'promo-code-load' });
        }
      }
    }
    catch (e) {
      console.log({ e });
      setData([]);
      setCount(0);
    }
    finally {
      if (requestId === requestRef.current) {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    }
  }

  // change pagination func
  const handlePagination = (value) => {
    setOffset(value);
  };

  // -----------------------------------------------------------------------
  // Filter panel
  // -----------------------------------------------------------------------

  const handleFilterOpen = () => {
    if (filterOpen) {
      setFilterOpen(false);
      return;
    }

    setPlanDraft(planFilter);
    setFilterOpen(true);
  };

  const handlePlanToggle = (plan) => {
    setPlanDraft((prev) =>
      prev.includes(plan)
        ? prev.filter((x) => x !== plan)
        : [...prev, plan]
    );
  };

  const handleFilterApply = () => {
    setPlanFilter(planDraft);
    setOffset(1);
    setFilterOpen(false);
  };

  // CLOSE removes every applied filter, so all records are shown again
  const handleFilterClose = () => {
    setPlanDraft([]);
    setPlanFilter([]);
    setOffset(1);
    setFilterOpen(false);
  };

  // -----------------------------------------------------------------------
  // Create / Edit dialog
  // -----------------------------------------------------------------------

  const handleClickOpen = () => {
    setForm(initialValues);
    setErrors({});
    setOpen(true);
  };

  const handleEdit = (row) => {
    setForm({
      id: row?.id,
      promo_code: row?.promo_code || '',
      promo_description: row?.promo_description || '',
      plan: row?.plan || '',
      trial_period: toTrialPeriodInput(row?.trial_period),
      discounted_price: toPriceInput(row?.discounted_price),
      start_date: toDateInput(row?.start_date),
      end_date: toDateInput(row?.end_date),
      is_valid: Boolean(row?.is_valid),
      is_active: Boolean(row?.is_active),
    });

    setErrors({});
    setOpen(true);
  };

  const handleClose = () => {
    setForm(initialValues);
    setErrors({});
    setOpen(false);
  };

  // update one field and clear its error message
  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handlePriceChange = (e) => {
    const value = e.target.value;

    // ignore anything that is not a valid amount while typing
    if (value === '' || PRICE_TYPING_REGEX.test(value)) {
      setField('discounted_price', value);
    }
  };

  const handlePlanChange = (e) => {
    const selectedPlan = e.target.value;

    setForm((prev) => ({
      ...prev,
      plan: selectedPlan,
      trial_period: selectedPlan === 'Trial' ? prev.trial_period : '',
    }));

    setErrors((prev) => ({
      ...prev,
      plan: undefined,
      trial_period: undefined,
    }));
  };

  const handleStartDateChange = (e) => {
    const value = e.target.value;

    setField('start_date', value);

    // keep the end date valid when the start date moves past it
    if (value && form.end_date && form.end_date < value) {
      setField('end_date', '');
    }
  };

  // -----------------------------------------------------------------------
  // Validation
  // -----------------------------------------------------------------------

  const validate = () => {
    const e = {};
    const code = form.promo_code.trim().toUpperCase();

    if (!code) {
      e.promo_code = 'Promo code is required';
    } else if (!PROMO_CODE_REGEX.test(code)) {
      e.promo_code = 'Use 3-20 letters, numbers, hyphen or underscore';
    }

    if (!form.promo_description.trim()) {
      e.promo_description = 'Description is required';
    } else if (form.promo_description.trim().length > 250) {
      e.promo_description = 'Maximum 250 characters';
    }

    if (!form.plan) {
      e.plan = 'Please select a plan';
    }

    // Trial period is mandatory only for Trial plan
    if (form.plan === 'Trial' && !form.trial_period) {
      e.trial_period = 'Please select a trial period';
    }

    // Discounted price is OPTIONAL: validate the format only when something is typed
    const price = String(form.discounted_price).trim();

    if (price !== '' && !PRICE_REGEX.test(price)) {
      e.discounted_price = 'Enter a valid amount (maximum 2 decimals)';
    }

    if (!form.start_date) {
      e.start_date = 'Start date is required';
    }

    if (!form.end_date) {
      e.end_date = 'End date is required';
    } else if (form.start_date && form.end_date < form.start_date) {
      e.end_date = 'End date cannot be before start date';
    }

    setErrors(e);

    return Object.keys(e).length === 0;
  };

  // -----------------------------------------------------------------------
  // Save
  // Duplicate check (same promo code + same plan) is done by the backend.
  // It returns a clear message which is shown below, so no extra list call
  // (the old limit:1000 call is what caused "Invalid offset or limit").
  // -----------------------------------------------------------------------

  async function handleSave() {
    if (!validate()) return;

    setSaving(true);

    try {
      const priceText = String(form.discounted_price).trim();

      const payload = {
        promo_code: form.promo_code.trim().toUpperCase(),
        promo_description: form.promo_description.trim(),
        plan: form.plan,

        // Send trial_period only for Trial plan (plain number 3,4,5,6)
        trial_period:
          form.plan === 'Trial'
            ? Number(form.trial_period)
            : null,

        // optional: null when left empty
        discounted_price:
          priceText === ''
            ? null
            : Number(priceText).toFixed(2),

        start_date: form.start_date,
        end_date: form.end_date,
        is_valid: form.is_valid, // One Year Application
        is_active: form.is_active,
      };

      let res = isEdit
        ? await updatePromoCode({ ...payload, id: form.id })
        : await createPromoCode(payload);

      if (res?.status_code == 200 && res?.type?.toLowerCase() !== 'error') {
        toast.success(res?.message, { toastId: 'promo-code-save' });

        handleClose();

        if (!isEdit && offset !== 1) {
          setOffset(1);
        } else {
          handleGetPromoCodes();
        }
      } else {
        // dialog stays open so the user can fix the value (e.g. duplicate code)
        if (res?.status_code == 409) {
          setErrors((prev) => ({ ...prev, promo_code: res?.message }));
        }

        toast.error(
          res?.message || 'Unable to save promo code',
          { toastId: 'promo-code-save' }
        );
      }
    }
    catch (e) {
      console.log({ e });

      toast.error(
        'Something went wrong. Please try again.',
        { toastId: 'promo-code-save' }
      );
    }
    finally {
      setSaving(false);
    }
  }

  // -----------------------------------------------------------------------
  // Export
  // -----------------------------------------------------------------------

  const handleExport = async () => {
    let values = {};

    const search = String(searchValue || "").trim();

    if (search) {
      values.searchValue = search;
    }

    if (planFilter.length > 0) {
      values.plans = planFilter;
    }

    dispatch({ type: "SET_LOADING", payload: true });
    setIsLoading(true);

    let res = await getPromoCodeExport(values);

    dispatch({ type: "SET_LOADING", payload: false });
    setIsLoading(false);

    if (res && res.data) {
      if (res.data.length === 0) {
        toast.success(t("No Data Found"), { toastId: "no-report-date" });
        return false;
      }

      setExportData(res.data);
      return true;
    }

    return false;
  };

  return (
    <>
      <div>
        <div className={classes.btnDiv}>
          <Grid item xs={12} style={{ display: "flex", alignItems: "center" }}>
            <div style={{ flex: 0.99 }}>
              <CssTextField
                placeholder="Search for promo code or description"
                type={"text"}
                variant="outlined"
                className={classes.searchInput}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {searchValue.length > 0 ? (
                        <CloseOutlinedIcon
                          className={classes.searchInputIcon}
                          onClick={() => {
                            setSearchValue("");
                            setOffset(1);
                          }}
                        />
                      ) : (
                        <img
                          src={searchIcon}
                          alt=""
                          width="20px"
                          className={classes.searchInputIcon}
                        />
                      )}
                    </InputAdornment>
                  ),
                }}
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                  setOffset(1);
                }}
              />
            </div>

            <button className={classes.filterBtn} onClick={handleFilterOpen}>
              {appliedFilterCount > 0
                ? `${t('Filter')} (${appliedFilterCount})`
                : t('Filter')}
            </button>

            <CSVLink
              id="csv"
              data={exportData}
              headers={exportHeaders.map((head) => head)}
              filename={
                "PromoCodes" +
                moment(new Date()).format("MM-DD-YYYY,h:mm A") +
                ".csv"
              }
              className={classes.CSVlink}
            />

            <button
              disabled={isLoading}
              className={classes.exportBtn}
              onClick={async () => {
                let ok = await handleExport();

                if (ok) {
                  // wait one tick so CSVLink has the new data
                  setTimeout(() => {
                    document.getElementById("csv").click();
                  }, 0);
                }
              }}
            >
              Export
            </button>

            <button className={classes.createBtn} onClick={handleClickOpen}>
              {t('Create')}
            </button>
          </Grid>
        </div>

        {filterOpen && (
          <div className={classes.filterPanel}>
            <div className={classes.filterColumn}>
              <div className={classes.filterHeading}>
                {t('Plan')}
              </div>

              <div className={classes.filterOptions}>
                {PLAN_OPTIONS.map((plan) => (
                  <FormControlLabel
                    key={plan}
                    control={
                      <Checkbox
                        color="primary"
                        size="small"
                        checked={planDraft.includes(plan)}
                        onChange={() => handlePlanToggle(plan)}
                      />
                    }
                    label={plan}
                  />
                ))}
              </div>

              <div className={classes.filterFooter}>
                <span
                  className={classes.filterClose}
                  onClick={handleFilterClose}
                >
                  CLOSE
                </span>

                <button
                  className={classes.applyFilterBtn}
                  onClick={handleFilterApply}
                >
                  {t('Apply Filters')}
                </button>
              </div>
            </div>
          </div>
        )}

        <Loader Loading={state.loading} top={'35%'}>
          <div className={classes.responsiveTable}>
            <Grid item xs={12} sm={12} lg={12}>
              <Table
                headings={headings}
                data={data.map((p, i) => [
                  ((offset - 1) * 10) + (i + 1),
                  [p?.promo_code],
                  [p?.promo_description],
                  [p?.plan],
                  [
                    p?.plan === 'Trial' && p?.trial_period
                      ? `${p.trial_period} Months`
                      : '-',
                  ],
                  [
                    <span key={i} style={{ fontWeight: 600 }}>
                      {formatPrice(p?.discounted_price)}
                    </span>,
                  ],
                  [toDateInput(p?.start_date) || '-'],
                  [toDateInput(p?.end_date) || '-'],
                  [p?.is_valid ? 'Yes' : 'No'],
                  [
                    <span
                      key={i}
                      style={{
                        color: p?.is_active ? '#2e7d32' : '#c62828',
                        fontWeight: 600,
                      }}
                    >
                      {p?.is_active ? 'Active' : 'Inactive'}
                    </span>,
                  ],
                  [
                    <EditIcon
                      key={i}
                      style={{ cursor: "pointer" }}
                      onClick={() => handleEdit(p)}
                    />,
                  ],
                ])}
                offset={offset}
                count={
                  (count / 10) % 1 == 0
                    ? Math.floor(count / 10)
                    : Math.floor(count / 10 + 1)
                }
                handlePagination={handlePagination}
              />
            </Grid>
          </div>
        </Loader>
      </div>

      {/* Create / Edit dialog */}
      <Dialog
        open={open}
        keepMounted
        onClose={handleClose}
        aria-describedby='promo-code-dialog'
        fullWidth
        maxWidth='sm'
        disableBackdropClick
      >
        <DialogTitle></DialogTitle>

        <DialogContent>
          <CloseOutlinedIcon
            className={classes.closeIcon}
            onClick={handleClose}
          />

          <div
            style={{
              fontSize: 20,
              fontWeight: 'bold',
              textAlign: 'center',
            }}
          >
            {isEdit ? t('Edit Promo Code') : t('Create Promo Code')}
          </div>

          <Grid container item xs={12} spacing={2}>
            <Grid item xs={12}>
              <CssTextField
                id='promo_code'
                label={t('Promo Code')}
                type={'text'}
                fullWidth
                disabled={isEdit}
                value={form.promo_code}
                inputProps={{ maxLength: 20 }}
                onChange={(e) => {
                  setField('promo_code', e.target.value.toUpperCase());
                }}
              />

              {errors.promo_code && (
                <span className={classes.errorMsg}>
                  {errors.promo_code}
                </span>
              )}

              {isEdit && (
                <span
                  style={{
                    fontSize: '12px',
                    color: 'gray',
                    display: 'block',
                    marginTop: '4px',
                  }}
                >
                  Promo code cannot be changed after creation
                </span>
              )}
            </Grid>

            <Grid item xs={12}>
              <CssTextField
                id='promo_description'
                label={t('Promo Description')}
                type={'text'}
                fullWidth
                multiline
                rowsMax={4}
                value={form.promo_description}
                inputProps={{ maxLength: 250 }}
                onChange={(e) => {
                  setField('promo_description', e.target.value);
                }}
              />

              {errors.promo_description ? (
                <span className={classes.errorMsg}>
                  {errors.promo_description}
                </span>
              ) : (
                <span
                  style={{
                    fontSize: '12px',
                    color: 'gray',
                    display: 'block',
                    marginTop: '4px',
                  }}
                >
                  {form.promo_description.length}/250
                </span>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <CssTextField
                id='plan'
                label={t('Plan')}
                select
                fullWidth
                value={form.plan}
                onChange={handlePlanChange}
              >
                {PLAN_OPTIONS.map((plan) => (
                  <MenuItem key={plan} value={plan}>
                    {plan}
                  </MenuItem>
                ))}
              </CssTextField>

              {errors.plan && (
                <span className={classes.errorMsg}>
                  {errors.plan}
                </span>
              )}
            </Grid>

            {/* Show Trial Period only when Trial plan is selected */}
            {form.plan === 'Trial' && (
              <Grid item xs={12} sm={6}>
                <CssTextField
                  id='trial_period'
                  label={t('Trial Period')}
                  select
                  fullWidth
                  value={form.trial_period}
                  onChange={(e) => {
                    setField('trial_period', e.target.value);
                  }}
                >
                  {TRIAL_PERIOD_OPTIONS.map((months) => (
                    <MenuItem key={months} value={months}>
                      {months} Months
                    </MenuItem>
                  ))}
                </CssTextField>

                {errors.trial_period && (
                  <span className={classes.errorMsg}>
                    {errors.trial_period}
                  </span>
                )}
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <CssTextField
                id='discounted_price'
                label={`${t('Discounted Price')} (${t('Optional')})`}
                type={'text'}
                fullWidth
                value={form.discounted_price}
                placeholder="0.00"
                inputProps={{
                  inputMode: 'decimal',
                  maxLength: 10,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      $
                    </InputAdornment>
                  ),
                }}
                onChange={handlePriceChange}
                onBlur={() => {
                  // show the amount with 2 decimals once the user leaves the box
                  const v = String(form.discounted_price).trim();

                  if (v !== '' && PRICE_REGEX.test(v)) {
                    setForm((prev) => ({
                      ...prev,
                      discounted_price: Number(v).toFixed(2),
                    }));
                  }
                }}
              />

              {errors.discounted_price && (
                <span className={classes.errorMsg}>
                  {errors.discounted_price}
                </span>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <CssTextField
                id='start_date'
                label={t('Start Date')}
                type={'date'}
                fullWidth
                value={form.start_date}
                InputLabelProps={{ shrink: true }}
                onChange={handleStartDateChange}
              />

              {errors.start_date && (
                <span className={classes.errorMsg}>
                  {errors.start_date}
                </span>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <CssTextField
                id='end_date'
                label={t('End Date')}
                type={'date'}
                fullWidth
                value={form.end_date}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: form.start_date || undefined,
                }}
                onChange={(e) => {
                  setField('end_date', e.target.value);
                }}
              />

              {errors.end_date && (
                <span className={classes.errorMsg}>
                  {errors.end_date}
                </span>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <CssTextField
                id='is_valid'
                label={t('Once Per Shipto')}
                select
                fullWidth
                value={form.is_valid}
                onChange={(e) => {
                  setField('is_valid', e.target.value === 'true' || e.target.value === true);
                }}
              >
                <MenuItem value={true}>
                  {t('Yes')}
                </MenuItem>

                <MenuItem value={false}>
                  {t('No')}
                </MenuItem>
              </CssTextField>

              
            </Grid>

            <Grid item xs={12} sm={6}>
              <CssTextField
                id='is_active'
                label={t('Is Active')}
                select
                fullWidth
                value={form.is_active}
                onChange={(e) => {
                  setField('is_active', e.target.value === 'true' || e.target.value === true);
                }}
              >
                <MenuItem value={true}>
                  {t('Active')}
                </MenuItem>

                <MenuItem value={false}>
                  {t('Inactive')}
                </MenuItem>
              </CssTextField>
            </Grid>
          </Grid>

          <div className={classes.popupBtnDiv}>
            <button
              className={classes.cancelBtn}
              onClick={handleClose}
              disabled={saving}
            >
              {t('Cancel')}
            </button>

            <button
              className={classes.submitBtn}
              onClick={handleSave}
              disabled={saving}
            >
              {!saving
                ? (isEdit ? t('Update') : t('Submit'))
                : `${t('Please wait')}..`}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default PromoCodeCreation;